// web/menuBackground.ts — the LIVING menu backdrop.
//
// The main menu is painted over a real, running colony — not a static image. We
// build a small headless GameSession, step it slowly, and render it with the same
// canvas renderer the game uses (so the backdrop genuinely shows foragers hauling
// resources, soldiers patrolling, pheromone supply-chains hardening, wildlife
// drifting through). A slow CINEMATIC camera drifts over it, and a dark gradient
// keeps the menu text legible. Strictly presentation: its own throwaway session,
// never saved, never the player's colony, no determinism/hash impact.

import { GameSession, DEFAULT_PARAMS } from './game.ts';
import { render } from './render.ts';
import { LogisticsView } from './logistics.ts';
import { WatchView } from './watch.ts';
import { DEFAULT_SETTINGS } from './save.ts';
import type { Settings } from './save.ts';

const BG_TPS = 5;           // slow, calm stepping — readable, never frantic
const WARMUP_TICKS = 260;   // pre-roll so the menu opens onto an ALREADY-living world
const MAX_BG_STEPS = 3;     // cap catch-up after a stall (e.g. a backgrounded tab)

export class MenuBackground {
  private ctx: CanvasRenderingContext2D | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private session: GameSession | null = null;
  private logistics: LogisticsView | null = null;
  private watch: WatchView | null = null;
  private readonly settings: Settings;
  private raf = 0;
  private last = 0;
  private acc = 0;
  private running = false;

  constructor() {
    this.settings = {
      ...DEFAULT_SETTINGS,
      showTrails: true,       // pheromone supply-chains + courier motes
      showHiveNodes: false,
      showGrid: false,
      showGenerations: false,
    };
  }

  /** Begin (or resume) animating into `canvas`. Cheap to call repeatedly. */
  start(canvas: HTMLCanvasElement): void {
    if (this.running) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.canvas = canvas;
    this.ctx = ctx;
    canvas.style.display = 'block';
    this.running = true;
    this.last = 0;
    this.raf = requestAnimationFrame(this.frame); // frame() safely no-ops until the world exists
    // Defer the ~90ms headless world build OFF the first menu paint so the menu appears
    // instantly (the calm gradient shows behind the empty canvas meanwhile). Idle when the
    // browser supports it, else a 0ms timeout. Guarded so a quick menu-close skips the build;
    // a failed build stops the loop and leaves the plain gradient.
    if (!this.session) {
      const buildWhenIdle = (): void => {
        if (!this.running || this.session) return;
        this.build();
        if (!this.session) this.stop();
      };
      const ric = (globalThis as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }).requestIdleCallback;
      if (typeof ric === 'function') ric(buildWhenIdle, { timeout: 500 });
      else setTimeout(buildWhenIdle, 0);
    }
  }

  /** Pause animating (keeps the world so a re-open resumes where it left off). */
  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.canvas) this.canvas.style.display = 'none'; // never sit over the game
  }

  private build(): void {
    const seed = Math.floor(Math.random() * 2 ** 31) >>> 0;
    // A COMPACT, lively world: small enough that a calm step shows dense, recognisable
    // activity framed whole by the drifting camera (the playable world is far bigger).
    const params = {
      ...DEFAULT_PARAMS, seed,
      width: 88, height: 52, colonies: 3, startMembers: 16,
      food: 80, foodAmount: 22, maxPerTile: 5,
    };
    try {
      const session = new GameSession(params);
      this.session = session;
      this.logistics = new LogisticsView(params.width, params.height, session.world.colonies.capacity);
      this.watch = new WatchView(session.world.entities.capacity);
      this.watch.setMode('cinematic'); // the slow "second-monitor" drift
      // Pre-roll so the colony is already foraging/expanding when the menu appears.
      session.step(WARMUP_TICKS);
      for (let i = 0; i < 30; i++) this.logistics.update(session, 1 / 30);
    } catch {
      this.session = null; // never let the backdrop take down the menu
    }
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);
    const session = this.session;
    const ctx = this.ctx;
    const canvas = this.canvas;
    if (!session || !ctx || !canvas) return;

    try {
      const dt = this.last ? Math.min((now - this.last) / 1000, 0.1) : 0;
      this.last = now;

      // Slow, bounded stepping — the colony moves at a calm, watchable pace.
      this.acc += dt * BG_TPS;
      let steps = Math.floor(this.acc);
      if (steps > 0) {
        this.acc -= steps;
        if (steps > MAX_BG_STEPS) steps = MAX_BG_STEPS;
        session.step(steps);
        this.logistics?.update(session, dt);
      }

      // Match the backing store to the element's CSS box (DPR-aware, capped).
      const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
      const cssW = canvas.clientWidth || (globalThis.innerWidth || 960);
      const cssH = canvas.clientHeight || (globalThis.innerHeight || 600);
      const bw = Math.round(cssW * dpr);
      const bh = Math.round(cssH * dpr);
      if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const W = session.world.grid.width;
      const H = session.world.grid.height;
      this.watch?.update(session, dt, W, H);
      render(ctx, cssW, cssH, session, this.settings, this.logistics, this.watch, null);

      // Atmospheric dim so the menu text stays legible over the living world.
      const g = ctx.createLinearGradient(0, 0, 0, cssH);
      g.addColorStop(0, 'rgba(7,9,14,0.50)');
      g.addColorStop(0.55, 'rgba(7,9,14,0.74)');
      g.addColorStop(1, 'rgba(7,9,14,0.92)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, cssW, cssH);
    } catch {
      this.stop(); // any renderer hiccup → drop the backdrop, keep the menu alive
    }
  };
}
